import React from 'react';
import { priceunit } from '../Components/client-config';
import {
  ICartItem,
  ICartItemSize,
  ICartItemCombo,
  ICouponDetails,
  IMenuItem,
  IMenuPriceMeta
} from '../lib/types';
import { COUPON_TYPES, COUPON_LEVELS, ORDER_CHANNELS } from '../lib/constants';
import { PriceDisplay } from '../Components/PriceDisplay/PriceDisplay';

export function getOriginalPrice(item: ICartItem): number {
  console.log("item");
  if (item.updateOnlineItemPrice && item.updateOnlineItemPrice > 0) {
    return item.updateOnlineItemPrice;
  }

  if (item.item && item.selectedsize) {
    return item.selectedsize.mrp * item.quantity;
  }

  if (item.combo && item.combo.combo_mrp_price) {
    return item.combo.combo_mrp_price * item.quantity;
  }

  return parseInt(item.totalItemPrice.toString());
}

export function getDiscountedPrice(item: ICartItem): number {
  if (item.updateOnlineItemPrice && item.updateOnlineItemPrice > 0) {
    return item.updateOnlineItemPrice;
  }

  return parseInt(item.totalItemPrice.toString());
}

export function hasItemDiscount(item: ICartItem): boolean {
  if (item.item && item.selectedsize) {
    const discountPrice = item.selectedsize.discount_price;
    return (
      (discountPrice !== null && discountPrice !== undefined && discountPrice >= 0) &&
      discountPrice < item.selectedsize.mrp
    );
  }

  if (item.combo && item.combo.combo_mrp_price) {
    const discountPrice = item.combo.discount_price;
    return (
      (discountPrice !== null && discountPrice !== undefined && discountPrice >= 0) &&
      discountPrice < item.combo.combo_mrp_price
    );
  }

  return false;
}

export function calculateItemDiscountAmount(item: ICartItem): number {
  if (!hasItemDiscount(item)) {
    return 0;
  }

  if (item.item && item.selectedsize) {
    const originalPrice = item.selectedsize.mrp * item.quantity;
    const discountedPrice = (item.selectedsize.discount_price || 0) * item.quantity;
    return originalPrice - discountedPrice;
  }

  if (item.combo && item.combo.combo_mrp_price) {
    const originalPrice = item.combo.combo_mrp_price * item.quantity;
    const discountedPrice = (item.combo.discount_price || 0) * item.quantity;
    return originalPrice - discountedPrice;
  }

  return 0;
}

export function calculateTotalItemDiscount(cartItems: ICartItem[]): number {
  let totalDiscount = 0;
  cartItems.forEach((item) => {
    totalDiscount += calculateItemDiscountAmount(item);
  });
  return Math.round(totalDiscount);
}

export function isValidDiscountForOnline(size: ICartItemSize | ICartItemCombo): boolean {
  const discountPrice = 'mrp' in size ? size.discount_price : size.discount_price;
  const orderChannel = 'order_channel' in size ? size.order_channel : undefined;
  
  return (
    (discountPrice !== null && discountPrice !== undefined && discountPrice >= 0) &&
    (orderChannel === ORDER_CHANNELS.ONLINE || orderChannel === ORDER_CHANNELS.ALL)
  );
}

export function calculateOriginalSubTotal(cartItems: ICartItem[]): number {
  let sum = 0;
  cartItems.forEach((item) => {
    sum += getOriginalPrice(item);
  });
  return Math.round(sum);
}

export function calculateSubTotal(cartItems: ICartItem[], useOriginalPrice: boolean = false): number {
  let sum = 0;
  cartItems.forEach((item) => {
    if (useOriginalPrice) {
      sum += getOriginalPrice(item);
    } else {
      sum += getDiscountedPrice(item);
    }
  });
  return sum;
}

export function subTotalCal(data: ICartItem[]): number {
  let sum = 0;
  data.forEach((item: ICartItem) => {
    sum += (item.updateOnlineItemPrice && item.updateOnlineItemPrice > 0) 
      ? item.updateOnlineItemPrice 
      : parseInt(item.totalItemPrice.toString());
  });
  return Math.round(sum);
}

export function couponDiscountCal(couponDetails: ICouponDetails, cart: ICartItem[]): number {
  let discount = 0;
  const { type: coupon_type, value } = couponDetails.coupon_type;
  const { type: coupon_level, list } = couponDetails.coupon_level;

  if (coupon_level === COUPON_LEVELS.GROUP) {
    const cartItemsWithGroups = cart.length > 0
      ? cart.filter((product: ICartItem) => {
        const group_id = product?.item?.item_group_id?.group_id || product?.combo?.group_id?.group_id;
        return list?.includes(group_id!);
      })
      : [];

    if (coupon_type === COUPON_TYPES.PERCENT) {
      const cartTotal = cartItemsWithGroups.length > 0
        ? calculateOriginalSubTotal(cartItemsWithGroups)
        : 0;
      discount = cartTotal * ((value || 0) / 100);
    } else if (coupon_type === COUPON_TYPES.VALUE) {
      discount = cartItemsWithGroups.length > 0 ? (value || 0) : 0;
    }
  } else {
    if (coupon_type === COUPON_TYPES.PERCENT) {
      discount = calculateOriginalSubTotal(cart) * ((value || 0) / 100);
    } else if (coupon_type === COUPON_TYPES.VALUE) {
      discount = value || 0;
    }
  }

  return Math.round(discount);
}


function getMenuPrice(menuItem: IMenuItem): IMenuPriceMeta {
  const isItem = !!(menuItem.item_size && menuItem.item_size.length > 0);

  let originalPrice = 0;
  let discountedPrice = 0;
  let hasDiscount = false;
  let discountLabel: string | null = null;
  let discountCurrency: string | null = null;
  let discountValue: string | null = null;
  let isPercentage: boolean = false;
  if (isItem && menuItem.item_size) {
    const size = menuItem.item_size[0];
    originalPrice = size.mrp;

    const isOnlineChannel =
      size.order_channel === ORDER_CHANNELS.ONLINE ||
      size.order_channel === ORDER_CHANNELS.ALL;

    hasDiscount =
      size.discount_price !== null &&
      size.discount_price !== undefined &&
      size.discount_price < size.mrp &&
      isOnlineChannel;

    discountedPrice =
      hasDiscount && size.discount_price !== null && size.discount_price !== undefined
        ? size.discount_price
        : originalPrice;

    if (hasDiscount) {
      if (size.discount_type !== COUPON_TYPES.VALUE && size.discount_value && size.discount_value > 0) {
        discountLabel = `${size.discount_value}% OFF`;
        discountValue = `${size.discount_value}%`;
        isPercentage = true;
      } else if (size.discount_value && size.discount_value > 0) {
        const unit = priceunit.trim();
        discountCurrency = unit;
        discountValue = `${size.discount_value}`;
        discountLabel = `${unit}${size.discount_value} OFF`;
        isPercentage = false;
      }
    }
  } else {
    const isOnlineChannel =
      menuItem.order_channel === ORDER_CHANNELS.ONLINE ||
      menuItem.order_channel === ORDER_CHANNELS.ALL;

    originalPrice = menuItem.combo_mrp_price || 0;

    hasDiscount =
      menuItem.discount_price !== null &&
      menuItem.discount_price !== undefined &&
      menuItem.combo_mrp_price !== undefined &&
      menuItem.discount_price < menuItem.combo_mrp_price &&
      isOnlineChannel;

    discountedPrice =
      hasDiscount && menuItem.discount_price !== null && menuItem.discount_price !== undefined
        ? menuItem.discount_price
        : originalPrice;

    if (hasDiscount) {
      if (menuItem.percent && menuItem.percent > 0) {
        discountLabel = `${menuItem.percent}% OFF`;
        discountValue = `${menuItem.percent}%`;
        isPercentage = true;
      } else if (menuItem.discount_value && menuItem.discount_value > 0) {
        const unit = priceunit.trim();
        discountCurrency = unit;
        discountValue = `${menuItem.discount_value}`;
        discountLabel = `${unit}${menuItem.discount_value} OFF`;
        isPercentage = false;
      }
    }
  }

  return {
    originalPrice,
    discountedPrice,
    hasDiscount,
    discountLabel,
    discountCurrency,
    discountValue,
    isPercentage,
  };
}

export const renderPriceDisplay = (cartItem: ICartItem, isOrderSummary?: boolean): React.ReactElement => {
  const hasDiscount = hasItemDiscount(cartItem);

  if (cartItem.item && cartItem.selectedsize) {
    const originalPrice = cartItem.selectedsize.mrp * cartItem.quantity;
    return (
      <PriceDisplay
        originalPrice={originalPrice}
        discountedPrice={cartItem.totalItemPrice}
        hasDiscount={hasDiscount}
        isOrderSummary={isOrderSummary}
      />
    );
  }

  if (cartItem.combo) {
    const originalPrice = cartItem.combo.combo_mrp_price * cartItem.quantity;
    return (
      <PriceDisplay
        originalPrice={originalPrice}
        discountedPrice={cartItem.totalItemPrice}
        hasDiscount={hasDiscount}
        isOrderSummary={isOrderSummary}
      />
    );
  }

  return (
    <span className='original-value' style={isOrderSummary ? { fontSize: '16px' } : undefined}>
      {priceunit}{cartItem.totalItemPrice}
    </span>
  );
};

export const renderMenuPriceDisplay = (menuItem: IMenuItem): React.ReactElement => {
  const { originalPrice, discountedPrice, hasDiscount } = getMenuPrice(menuItem);

  return (
    <PriceDisplay
      originalPrice={originalPrice}
      discountedPrice={discountedPrice}
      hasDiscount={hasDiscount}
    />
  );
};

export const getMenuDiscountLabel = (menuItem: IMenuItem): string | null => {
  const { discountLabel } = getMenuPrice(menuItem);
  return discountLabel;
};

export const getMenuDiscountMeta = (menuItem: IMenuItem) => {
  const { discountCurrency, discountValue, isPercentage, hasDiscount } = getMenuPrice(menuItem);
  return { discountCurrency, discountValue, isPercentage, hasDiscount };
};
